import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TwoFactorAuthService } from '../../../services/two-factor/two-factor-auth.service';
import { AuthService } from '../../../services/auth.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-verify-2fa-modal',
  templateUrl: './verify-2fa-modal.component.html',
  styleUrls: ['./verify-2fa-modal.component.css']
})
export class Verify2FAModalComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() isVisible: boolean = false;
  @Output() verified = new EventEmitter<boolean>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() codeReceived = new EventEmitter<string>();

  verifyForm: FormGroup;
  isLoading: boolean = false;
  attemptsLeft: number = 3;

  constructor(
    private fb: FormBuilder,
    private twoFactorService: TwoFactorAuthService,
    private authService: AuthService,
    private afs: AngularFirestore
  ) {
    this.verifyForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
    });
  }

  ngOnInit() {
    // Auto-focus en el input cuando se abre el modal
    if (this.isVisible) {
      setTimeout(() => {
        const input = document.getElementById('code') as HTMLInputElement;
        if (input) input.focus();
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isVisible']) {
      if (this.isVisible) {
        this.showModal();
      } else {
        this.hideModal();
      }
    }
  }

  ngAfterViewInit() {
    if (this.isVisible) {
      this.showModal();
    }
  }

  private showModal() {
    setTimeout(() => {
      const modalElement = document.getElementById('verify2FAModal');
      if (modalElement) {
        // Usar Bootstrap 5 para mostrar el modal
        const modal = new (window as any).bootstrap.Modal(modalElement, {
          backdrop: 'static',
          keyboard: false
        });
        modal.show();
        
        // Auto-focus en el input
        setTimeout(() => {
          const input = document.getElementById('code') as HTMLInputElement;
          if (input) input.focus();
        }, 100);
      }
    }, 50);
  }

  private hideModal() {
    const modalElement = document.getElementById('verify2FAModal');
    if (modalElement) {
      const modalInstance = (window as any).bootstrap?.Modal?.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
  }

  onCodeInput(event: any) {
    const value = event.target.value.replace(/\D/g, ''); // Solo números
    this.verifyForm.patchValue({ code: value });
  }

  async onSubmit() {
    if (this.verifyForm.invalid) return;

    this.isLoading = true;
    
    try {
      const currentUser = this.authService.userAuth.getValue();
      if (!currentUser?.uid) {
        throw new Error('Usuario no autenticado');
      }

      // Verificar el código 2FA
      const twoFactorData = await this.twoFactorService.getTwoFactorData(currentUser.uid);
      
      if (!twoFactorData?.secret) {
        throw new Error('2FA no configurado');
      }
      
      const isValid = this.twoFactorService.verifyToken(
        this.verifyForm.value.code,
        twoFactorData.secret
      );
      
      if (isValid) {
        const code = this.verifyForm.value.code;
        console.log('[verify-2fa-modal] Código 2FA válido:', code);
        console.log('[verify-2fa-modal] Longitud del código:', code.length);
        
        // Actualizar última vez usado (no crítico, continuar aunque falle)
        try {
          await this.afs.collection('two-factor-auth').doc(currentUser.uid).update({
            lastUsed: new Date()
          });
          console.log('[verify-2fa-modal] lastUsed actualizado correctamente');
        } catch (updateError) {
          console.warn('[verify-2fa-modal] Error al actualizar lastUsed (no crítico):', updateError);
          // Continuar con el flujo aunque falle la actualización
        }
        
        // Emitir código 2FA primero para que se guarde en el componente padre
        console.log('[verify-2fa-modal] Emitiendo código 2FA...');
        this.codeReceived.emit(code);
        
        // Usar Promise.resolve para asegurar que el código se guarde antes de continuar
        await Promise.resolve();
        
        // Emitir éxito después de que el código se haya guardado
        console.log('[verify-2fa-modal] Emitiendo evento verified con valor: true');
        this.verified.emit(true);
        console.log('[verify-2fa-modal] Evento verified emitido');
        
        // Cerrar modal después de emitir el evento
        this.closeModal();
      } else {
        this.attemptsLeft--;
        
        if (this.attemptsLeft <= 0) {
          this.cancelled.emit();
          this.closeModal();
          Swal.fire({ title: 'Error', text: 'Demasiados intentos fallidos. Operación cancelada.', icon: 'error', confirmButtonColor: '#ff8c00' });
        } else {
          Swal.fire({ title: 'Error', text: `Código incorrecto. Te quedan ${this.attemptsLeft} intentos.`, icon: 'error', confirmButtonColor: '#ff8c00' });
          this.verifyForm.patchValue({ code: '' });
        }
      }
    } catch (error: any) {
      console.error('[verify-2fa-modal] Error al verificar 2FA:', error);
      console.error('[verify-2fa-modal] Error details:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      
      let errorMessage = 'Ha ocurrido un error durante la verificación';
      if (error?.message) {
        errorMessage = error.message;
      }
      
      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#ff8c00'
      });
    } finally {
      this.isLoading = false;
    }
  }

  closeModal() {
    this.verifyForm.reset();
    this.attemptsLeft = 3;
    this.isVisible = false;
    this.hideModal();
  }

  onCancel() {
    this.cancelled.emit();
    this.closeModal();
  }
}
